import { api, cachedRequest } from '../apiClient';
import { apiCache } from '../cache';
import { User } from '../../types';

export const authService = {
  getCurrentUser: async (forceRefresh: boolean = false): Promise<{ user: User; is_authenticated: boolean }> => {
    return cachedRequest(
      'auth_user',
      async () => {
        const response = await api.get('/auth/user/');
        return response.data;
      },
      2 * 60 * 1000, // 2 minutes
      forceRefresh
    );
  },
  
  login: async (usernameOrEmail: string, password: string): Promise<{ user: User; is_authenticated: boolean; message: string; session_key?: string }> => {
    const response = await api.post('/auth/login/', {
      username_or_email: usernameOrEmail,
      password: password
    });
    
    // Clear user cache after login
    apiCache.clearUserCache();
    
    return response.data;
  },
  
  signup: async (userData: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    password: string;
    phone_number?: string;
    date_of_birth?: string;
  }): Promise<{ user: User; is_authenticated: boolean; message: string; session_key?: string }> => {
    const response = await api.post('/auth/signup/', userData);
    
    // Clear user cache after signup
    apiCache.clearUserCache();
    
    return response.data;
  },
  
  logout: async (): Promise<{ message: string; is_authenticated: boolean }> => {
    const response = await api.post('/auth/logout/');
    
    // Clear all user-related cache
    apiCache.clearUserCache();
    
    return response.data;
  },
  
  checkStatus: async (forceRefresh: boolean = false): Promise<{ is_authenticated: boolean; user: User | null; session_key?: string }> => {
    // Always bypass cache for auth status checks - we need fresh data
    // Auth status should never be cached as it can change at any time
    const response = await api.get('/auth/status/');
    return response.data;
  },

  checkSync: async (sessionKey?: string | null): Promise<{ 
    is_authenticated: boolean; 
    user: User | null; 
    session_key: string | null;
    session_changed: boolean;
    timestamp: string;
  }> => {
    const params = sessionKey ? { session_key: sessionKey } : {};
    const response = await api.get('/auth/sync/', { params });
    return response.data;
  },
  
  requestPasswordReset: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/password/reset/', {
      email: email
    });
    return response.data;
  },
  
  confirmPasswordReset: async (uid: string, token: string, password: string, confirmPassword: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/password/reset/confirm/', {
      uid: uid,
      token: token,
      password: password,
      confirm_password: confirmPassword
    });
    return response.data;
  },

  googleLogin: async (idToken: string, accessToken?: string): Promise<{ user: User; is_authenticated: boolean; message: string; session_key?: string }> => {
    const response = await api.post('/auth/google/', {
      id_token: idToken,
      access_token: accessToken
    });
    
    // Clear user cache after login
    apiCache.clearUserCache();
    
    return response.data;
  },

  googleOAuthCallback: async (code: string, redirectUri?: string): Promise<{ user: User; is_authenticated: boolean; message: string; session_key?: string }> => {
    const response = await api.post('/auth/google/callback/', {
      code: code,
      redirect_uri: redirectUri
    });
    
    // Clear user cache after login
    apiCache.clearUserCache();
    
    return response.data;
  }
};

